import { configureAudio, unlockAudio, setAudioEnabled, disposeAudio } from "yage/audio/runtime";
import { registerAirportSounds } from "yage/audio/presets/airport";
import { prepareAirportAudio } from './services/audio-buffers.js';
import "./style.css";
import "yage/style.css";
import "yage/schemas";
import "./components/index.js";
import { QuickStart } from "yage/game/QuickStart";
import { EntityFactory, type FlexibleEntityDefinition } from "yage/entity/EntityFactory";
import AssetLoader from "yage/loader/AssetLoader";
import { PlayerInput } from "yage/schemas/core/PlayerInput";
import { RigidBox } from "yage/schemas/physics/RigidBox";
import { Transform } from "yage/schemas/entity/Transform";
import { PixiViewportSystem } from "yage/systems/render/PixiViewport";
import { GameInstance } from "yage/game/GameInstance";
import { InputManager, InputEventType } from "yage/inputs/InputManager";
import { KeyboardListener } from "yage/inputs/KeyboardListener";
import { CoopConnectionInstance } from "yage/connection/CoopConnectionInstance";
import { loadAirportArt } from "./render/sprites.js";
import { buildWorld, joinPlayer, leavePlayer, state, career, stations, ids } from "./services/world.js";
import { loadProfiles, loadCareer, initialCareer, saveCareer, CAREERS, personIdentity, connectFamily, familySave, rememberedFamily, useFamily, savedCareerExists, startNewCareer } from "./services/persistence.js";
import { AirportAgent, AirportPassenger, AirportBag, AirportStaff, AirportZone, AirportPlane, AirportPath, AirportTrash, AirportVending } from "./components/index.js";
import { AirportScene } from "./systems/Scene.js";
import { captureOperations } from "./services/operations.js";
import { objectiveAction, guidanceTarget } from "./services/guidance.js";
import { currentMission } from "./domain/missions.js";
import { unlockCost, managerCost, upgradeCost } from "./domain/economy.js";
import { LOCAL } from "./services/local.js";
import { cameraDirection, projectWorld } from './render/camera.js';
import { GAME_VERSION } from "./domain/content-version.js";
import { checkpoints, checkpoint, TEST_TOOLS } from "./dev/checkpoints.js";
import { showSaveTools } from "./ui/save-tools.js";
import { showLobby, randomCode } from "./ui/lobby.js";
import { showOfflinePlay, offlineHostCode } from "./ui/offline-play.js";
import { registerOfflineWorker } from "./services/offline-pack.js";
import { watchGraphicsContext, reloadIntoGame, resumeRequested } from "./render/context-recovery.js";
import type { ManualPeer } from "yage/connection/ManualPeer";
// These are real YAGE/MineCS systems, not adapters around a second game loop.
import.meta.glob("./systems/**/*.ts", { eager: true });
const definitions = Object.values(import.meta.glob<FlexibleEntityDefinition>("./entities/**/*.json5", { eager: true, import: "default" }));
configureAudio(audio => {
  registerAirportSounds(audio);
  audio.define('idle-cash-pickup', {
    name: 'Cash pickup', category: 'airport-game', duration: 0.28, cooldown: 0.28, gain: 0.8,
    play: (voice, time) => {
      // A pitch-rising paper flick per bill, closed by a soft register ching (doc 10 §4.3).
      // The cooldown exceeds the ladder span so chained pickups never overlap, keeping the
      // worst-case bus sum at the old two-tone headroom (~0.14) instead of stacking ladders.
      [2100, 2600, 3200, 3900].forEach((f, i) => voice.tone(time + i * 0.04, { f, dur: 0.05, peak: 0.07, attack: 0.001 }));
      voice.tone(time + 0.17, { f: 5200, dur: 0.1, peak: 0.045, attack: 0.002 });
    },
  });
});
const unlockAirportAudio = () => { setAudioEnabled(!LOCAL.muted && !LOCAL.model?.paused && !document.hidden); unlockAudio(); };
window.addEventListener('pointerdown', unlockAirportAudio);
window.addEventListener('keydown', unlockAirportAudio);
document.addEventListener('visibilitychange', () => setAudioEnabled(!LOCAL.muted && !document.hidden && !LOCAL.model?.paused));
window.addEventListener('pagehide', () => { void disposeAudio(); });
const params = new URLSearchParams(location.search);
if ((params.has('seed') || params.has('mode') || params.has('speed')) && !TEST_TOOLS) throw new Error('Simulation overrides require a development or authorized test build.');
if (params.has('seed') && !/^\d{1,10}$/.test(params.get('seed')!)) throw new Error('Seed must be an unsigned integer.');
if (params.has('mode') && !['solo','coop'].includes(params.get('mode')!)) throw new Error('Mode must be solo or coop.');
if (params.has('speed') && params.get('speed')!=='1') throw new Error('This prototype supports speed=1. Use the E2E frame controls for accelerated testing.');
let preloadPromise: Promise<void> | undefined;
/** Boot phase marks (read by tools/measure-peer-join.mjs); they never affect simulation. */
const mark = (name: string) => performance.mark('sky-harbor:' + name);
const timed = <T,>(name: string, work: Promise<T>) => work.then(value => { mark(name); return value; });
function preload() {return preloadPromise ??= (async()=>{mark('preload-start');EntityFactory.configureEntityFactory(definitions);await timed('assets',AssetLoader.getInstance().load());
    const audio = unlockAudio();
    // Phones can hold Web Audio suspended (no tap yet, or the camera holds the audio session); sound is not needed to
    // start, so never let it stall the boot.
    if (audio) await timed('audio', Promise.race([prepareAirportAudio(audio).catch(error => console.warn('Airport audio unavailable.', error)), new Promise<void>(resolve => setTimeout(resolve, 5000))]));
    await Promise.all([timed('art',loadAirportArt()),timed('profiles',loadProfiles()),timed('career',loadCareer(params.has('fresh'),checkpoint(params.get('jump'))))]);mark('preload-done');})();}
const PEER_HOST = params.get('peer') || import.meta.env.VITE_PEER_HOST || 'peer.yage.games:443/peerjs';

// One captain per browser tab; peer rooms identify the tab by this id.
const netId = 'captain-' + Math.random().toString(36).slice(2, 6).toUpperCase();
const shared = { gameName: 'Sky Harbor', roomId: 'sky-harbor', seed: GAME_VERSION + (params.has('seed') ? ':seed:'+params.get('seed') : ''), buildWorld: (g: Parameters<typeof buildWorld>[0]) => buildWorld(g,{...initialCareer,relaxed:LOCAL.relaxed}), onPlayerJoin: (g: Parameters<typeof joinPlayer>[0],pid:string,config?:{personId?:string}|null) => joinPlayer(g,pid,config??{personId:personIdentity(LOCAL.profiles?.activeId??pid)}), onPlayerLeave: leavePlayer, preload };

function fatal(error: unknown) {
    console.error('Sky Harbor could not start', error);
    alert('Sky Harbor could not start. This build requires the supplied YAGE engine and a working WebGL or Canvas 2D renderer. See the browser console for the exact error.');
}

function wireDiagnostics<T>(instance: GameInstance<T>) {
    const rooms = instance.options.connection.roomStates;
    const model = Object.values(rooms).map(r => r.gameModel).find(Boolean);
    if (!model)
        throw new Error('YAGE did not initialize an airport world.');
    // Diagnostics are read-only in normal play. Test-only event injection is gated by ?e2e=true.
    const snapshot = () => ({ frame: model.frame, loaded: state(model).loaded, operations: captureOperations(model), economy: { ...state(model), ...career(model) }, stations: stations(model).map(s => ({ ...s })),
        agents: ids(model, AirportAgent).map(id => ({ id, pid: model.getTypedUnsafe(PlayerInput, id).pid, x: model.getTypedUnsafe(Transform, id).x, y: model.getTypedUnsafe(Transform, id).y, ...model.getTypedUnsafe(AirportAgent, id), moving: model.getTypedUnsafe(AirportPath, id).moving })),
        passengers: ids(model, AirportPassenger).length, bags: ids(model, AirportBag).map(id => ({ id, ...model.getTypedUnsafe(AirportBag, id), x: model.getTypedUnsafe(Transform, id).x, y: model.getTypedUnsafe(Transform, id).y })), staff: ids(model, AirportStaff).length,
        zones: ids(model, AirportZone).map(id => ({ id, ...model.getTypedUnsafe(AirportZone, id), x: model.getTypedUnsafe(Transform, id).x, y: model.getTypedUnsafe(Transform, id).y })),
        trash: ids(model, AirportTrash).map(id => ({ id, ...model.getTypedUnsafe(AirportTrash, id), x: model.getTypedUnsafe(Transform, id).x, y: model.getTypedUnsafe(Transform, id).y })),
        vending: ids(model, AirportVending).map(id => ({ ...model.getTypedUnsafe(AirportVending, id) })),
        walls: ids(model, RigidBox).map(id => ({ x: model.getTypedUnsafe(Transform, id).x, y: model.getTypedUnsafe(Transform, id).y, ...model.getTypedUnsafe(RigidBox, id) })),
        planes: ids(model, AirportPlane).map(id => ({ ...model.getTypedUnsafe(AirportPlane, id) })),
    });
    if (import.meta.env.DEV && params.has('dev')) showSaveTools(model, checkpoints);
    window.__SKY_HARBOR__ = { engine: 'YAGE', renderer: 'PixiViewportSystem', snapshot, cameraDirection };
    if (params.get('e2e') === 'true')
        Object.assign(window.__SKY_HARBOR__, {
            save: () => saveCareer(model),
            connectFamily: async (config:Parameters<typeof connectFamily>[0]) => {const result=await connectFamily(config);if(result.ok){const url=new URL(location.href);url.searchParams.delete('jump');url.searchParams.delete('fresh');location.assign(url);}return result;},
            exportSave: () => CAREERS.export(),
            importSave: async (text:string) => {const result=await CAREERS.import(text);if(result.ok){const url=new URL(location.href);url.searchParams.delete('jump');url.searchParams.delete('fresh');location.assign(url);}return result;},
            checkpoints: () => checkpoints,
            jump: (id:string) => {if(!checkpoint(id)) throw new Error('Unknown checkpoint');const url=new URL(location.href);url.searchParams.set('jump',id);location.assign(url);},
            travelers: () => ids(model, AirportPassenger).map(id => ({ id, ...model.getTypedUnsafe(AirportPassenger, id), x: model.getTypedUnsafe(Transform, id).x, y: model.getTypedUnsafe(Transform, id).y })),
            pads: () => [...(model.getSystem(AirportScene) as any).zones].map(([id, art]: [number, { pad: { visible: boolean } }]) => ({ id, visible: art.pad.visible })),
            command: (name: string, data: unknown = {}, slot = 0) => { const id = model.players[slot]; if (id !== undefined)
                model.event(model.getTypedUnsafe(PlayerInput, id).pid, 'airport:' + name, data); },
            worldToScreen: (x: number, y: number) => { const point = projectWorld(x, y), p = model.getSystem(PixiViewportSystem).viewport.toScreen(point.x, point.y); return { x: p.x, y: p.y }; },
            puzzle: () => model.getSystem(AirportScene).hud?.modal.snapshot(),
            serialized: () => model.serializeState(),
            buttons: () => {
                const out: {
                    text: string;
                    x: number;
                    y: number;
                    width: number;
                    height: number;
                }[] = [];
                const visit = (node: any) => { if (!node.visible)
                    return; if (typeof node.name === 'string' && node.name.startsWith('button:')) {
                    const b = node.getBounds();
                    out.push({ text: node.name.slice(7), x: b.x, y: b.y, width: b.width, height: b.height });
                } for (const child of node.children ?? [])
                    visit(child); };
                const hud = model.getSystem(AirportScene).hud;
                if (hud)
                    visit(hud.root);
                return out;
            },
            open: (kind: string) => model.getSystem(AirportScene).hud?.modal.open(kind),
            close: () => model.getSystem(AirportScene).hud?.modal.close(),
            guidance: (slot = 0) => { const id = model.players[slot]; const e = state(model), ss = stations(model), action = objectiveAction(model, id);
                return { action, target: guidanceTarget(model, id, action), mission: currentMission(e), index: e.mission, money: career(model).money,
                    costs: ss.map((s, i) => ({ build: unlockCost(i, e) - s.invested, hire: managerCost(i, e, e.hires), capacity: upgradeCost(s, 'capacity', e), speed: upgradeCost(s, 'speed', e), income: upgradeCost(s, 'income', e) })) }; },
        });
}

async function startCoop(count:number) {
                await shared.preload();
                // Local captains share one keyboard/touch screen; the captain
                // button in the HUD switches the active one.
                const inputManager = new InputManager(false);
                const keyboardListener = new KeyboardListener(inputManager);
                keyboardListener.init(['w', 'a', 's', 'd']);
                const players = Array.from({ length: count }, (_,index) =>
                    [InputEventType.KEYBOARD, 0, {personId:personIdentity(LOCAL.profiles?.profiles[index]?.id??'captain-'+(index+1))}] as [InputEventType, number, {personId:string}]);
                const connection = new CoopConnectionInstance<{personId:string}>(inputManager, players);
                await connection.connect();
                const instance = new GameInstance({
                    connection,
                    uiService: true,
                    buildWorld: shared.buildWorld,
                    onPlayerJoin: shared.onPlayerJoin,
                    onPlayerLeave: shared.onPlayerLeave,
                });
                await instance.initializeRoom(shared.roomId, shared.seed);
                wireDiagnostics(instance);
}

/** `peer` replaces the signaling server with a link made by swapping QR codes (offline play); `code` is then a full peer id. */
async function startPeer(mode: 'host' | 'join', code: string, peer?: ManualPeer) {
    await Promise.race([shared.preload(), new Promise<never>((_, reject) => setTimeout(() => reject(new Error('The airport took too long to load on this device. Try again.')), 45000))]);
    const playerConnect = { netId, uniqueId: netId, token: netId, config: {personId:familySave?.config.personId??personIdentity(LOCAL.profiles?.activeId??netId)} };
    const multiplayerConfig = {
        prefix: GAME_VERSION + '-', host: PEER_HOST, address: code,
        requireExistingHost: mode === 'join',
        frameOffset: 8, adaptiveInputDelay: false,
        // Joiners materialize 60 frames after the state is sent; lockstep keeps this correct at any value, and
        // 240 frames alone pushed measured joins past the 5 s budget (docs/evidence/peer-join-timing.json).
        joinGraceFrames: 60, disconnectTimeoutMs: 10000,
        peerFactory: peer && (() => peer),
    };
    mark('connect-start');
    const boot = QuickStart({ ...shared, connection: 'PEER' }, playerConnect, multiplayerConfig).then(instance => { mark('room-ready'); return instance; });
    const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Timed out reaching the multiplayer room.')), 15000));
    wireDiagnostics(await Promise.race([boot, timeout]));
}
/** Two devices on a hotspot or plain Wi-Fi with no internet: swap QR codes instead of using the signaling server. */
function startOffline(hostCode: string | null = null) {
    LOCAL.solo = false;
    return showOfflinePlay({
        prefix: GAME_VERSION + '-', hostCode, preload: shared.preload,
        startHost: peer => startPeer('host', peer.id, peer),
        startJoin: (peer, hostId) => startPeer('join', hostId, peer),
    });
}
/**
 * Family play needs no menu: join whoever is already playing the family career, otherwise open its room and keep the
 * claim fresh so the next family member lands here. The host's game autosaves the shared career; guests never write.
 */
async function startFamily(): Promise<boolean> {
    const config = params.has('join') ? null : rememberedFamily(params);
    if (!config) return false;
    const remote = useFamily(config);
    LOCAL.solo = false;
    // A guest whose host left reloads: the host saved on the way out, so this device resumes the career and hosts it.
    const follow = (code: string) => setInterval(async () => { const room = await remote.liveRoom(); if (room !== undefined && room?.code !== code) location.reload(); }, 5000);
    // An unreachable family server (no internet) shows the start menu, which offers offline play with the local copy.
    const live = await Promise.race([remote.liveRoom(), new Promise<undefined>(resolve => setTimeout(resolve, 4000))]);
    if (live === undefined) return false;
    if (live) {
        try { await startPeer('join', live.code); follow(live.code); return true; }
        catch (error) { console.warn('Family room could not be joined; opening a new one.', error); }
    }
    const code = randomCode();
    const claim = await remote.claimRoom(code);
    if (!claim.ok && claim.taken) { await startPeer('join', claim.taken.code); follow(claim.taken.code); return true; }
    try { await startPeer('host', code); }
    catch (error) {
        // No peer signalling (offline): play alone; the career still autosaves to the family server when it can.
        console.warn('Family room unavailable; playing solo.', error);
        remote.releaseRoom(code);
        LOCAL.solo = true;
        await shared.preload();
        wireDiagnostics(await QuickStart({ ...shared, connection: 'SINGLEPLAYER' }, {personId:config.personId??personIdentity(LOCAL.profiles?.activeId??'captain-1')}));
        return true;
    }
    setInterval(() => { remote.claimRoom(code); }, 15000);
    addEventListener('pagehide', () => remote.releaseRoom(code));
    return true;
}
async function start() {
    registerOfflineWorker();
    if (TEST_TOOLS && params.get('mode')==='coop') {LOCAL.solo=false;await startCoop(2);return;}
    const hostCode = offlineHostCode();
    // A pairing code works once: drop it from the address so a reload does not retry a stale one.
    if (hostCode) history.replaceState(null, '', location.pathname + location.search);
    if (hostCode) {
        try { await startOffline(hostCode); return; }
        catch { history.replaceState(null, '', location.pathname + location.search); }
    }
    if (!params.has('e2e') && !hostCode && navigator.onLine !== false && await startFamily()) return;
    if (!params.has('e2e')) {
        const startSolo = async (newGame = false) => {
            await shared.preload();
            if (newGame) startNewCareer();
            wireDiagnostics(await QuickStart({ ...shared, connection: 'SINGLEPLAYER' }, {personId:familySave?.config.personId??personIdentity(LOCAL.profiles?.activeId??'captain-1')}));
            // A blank canvas after the tab was hidden: save and come straight back.
            watchGraphicsContext(async () => {
                const g = LOCAL.model;
                if (g) await Promise.race([saveCareer(g), new Promise(resolve => setTimeout(resolve, 1500))]);
                reloadIntoGame();
            });
        };
        if (resumeRequested() && await savedCareerExists()) { LOCAL.solo = true; await startSolo(); return; }
        showLobby(async choice => {
            LOCAL.solo = choice.mode === 'solo';
            if (choice.mode === 'solo') {
                await startSolo(choice.newGame);
                return;
            }
            if (choice.mode === 'co-op') {
                await startCoop(choice.captains ?? 2);
                return;
            }
            if (choice.mode === 'offline') {
                await startOffline();
                return;
            }
            await startPeer(choice.mode === 'join' ? 'join' : 'host', choice.code!);
        }, params.get('join'), savedCareerExists());
        return;
    }
    await shared.preload();
                wireDiagnostics(await QuickStart({ ...shared, connection: 'SINGLEPLAYER' }, {personId:familySave?.config.personId??personIdentity(LOCAL.profiles?.activeId??'captain-1')}));
}
start().catch(fatal);
